import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useFormContext, useWatch } from "react-hook-form";

import isEqual from "lodash/isEqual";
import { commonSiteCoreKeys } from "../../../../../../../../helpers/constants/siteCoreKeys";
import {
  getFullName,
  getValueOrBlank,
  isEmpty,
} from "../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";

import { WarningConfirmationModal } from "../../../../../../../../components/modal/WarningModal";
import { SalesCreditDetailsWrapper } from "./SalesCreditDetails.style";
import { ProjectSalesCreditDetailsTable } from "./salesCreditDetailsTable/SalesCreditDetailsTable";
import {
  SalesCreditTableRow,
  ProjectSalesCreditDetailsFormKeys,
} from "../../../../../../modelsAndSchemas/Models";
import { salesCreditSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { UserDetails } from "../../../../../../../../helpers/types/common";

export interface ProjectSalesCreditDetailsProps {
  pageId: string;
  isInViewMode?: boolean;
  resultsFormKey: string;
  clientRelationshipOwnerList: UserDetails[];
  isEditProject?: boolean;
}

export const ProjectSalesCreditDetails = forwardRef(
  (
    {
      pageId,
      isInViewMode = false,
      resultsFormKey,
      clientRelationshipOwnerList,
      isEditProject = false,
    }: ProjectSalesCreditDetailsProps,
    ref,
  ) => {
    const { fetchValidationMessage, commonLabels, fetchLabel } = useSiteCore();
    const { getValues, setValue, setError } = useFormContext();
    const selectedDeleteSalesCreditRef = useRef<SalesCreditTableRow>();
    const [showDefaultLCROEntry, setShowDefaultLCROEntry] =
      useState<boolean>(!isEditProject);
    const [filteredResults, setFilteredResults] = useState<
      SalesCreditTableRow[]
    >([]);

    const [onSalesCreditAddedCallback, setOnSalesCreditAddedCallback] =
      useState<boolean>(false);
    const [onSalesCreditDeletedCallback, setonSalesCreditDeletedCallback] =
      useState<boolean>(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);

    const clientRelationshipOwnerKey =
      ProjectSalesCreditDetailsFormKeys.clientRelationshipOwner;
    const percentageKey = ProjectSalesCreditDetailsFormKeys.percentage;
    const relationshipOwnerOfficeKey =
      ProjectSalesCreditDetailsFormKeys.officeName;

    useImperativeHandle(ref, () => ({
      resetData,
    }));

    const resetData = () => {
      clearAllValues();
    };

    const clearAllValues = () => {
      setFilteredResults([]);
      clearInputValues();
    };

    const watchSalesCreditEntriesKey = useWatch({
      name: resultsFormKey,
    });

    const handleSalesCreditDeleteClick = (row: SalesCreditTableRow) => {
      const existFilteredResults = [...filteredResults];
      const index = existFilteredResults.findIndex((item) => {
        return item.clientRelationshipOwner === row.clientRelationshipOwner;
      });
      existFilteredResults.splice(index, 1);
      setShowDefaultLCROEntry(false);
      sortFilterData(existFilteredResults);
      setFilteredResults(existFilteredResults);
      addEntriesToFormKey(existFilteredResults, true);
      setonSalesCreditDeletedCallback(!onSalesCreditDeletedCallback);
    };

    const sortFilterData = (filteredResults: SalesCreditTableRow[]) => {
      filteredResults.sort((a, b) => {
        return a.clientRelationshipOwner.localeCompare(
          b.clientRelationshipOwner,
        );
      });
    };
    useEffect(() => {
      const salesCreditEntriesKeyValue = getValues(resultsFormKey);
      if (isEmpty(salesCreditEntriesKeyValue)) {
        setFilteredResults([]);
        return;
      }
      if (!isEqual(filteredResults, salesCreditEntriesKeyValue)) {
        const existFilteredResults: SalesCreditTableRow[] =
          salesCreditEntriesKeyValue.map(
            (salesCreditItem: SalesCreditTableRow) => {
              const firstName = getValueOrBlank(salesCreditItem?.firstName);
              const lastName = getValueOrBlank(salesCreditItem?.lastName);
              return {
                id: salesCreditItem.id,
                localClientRelationshipOwnerId:
                  salesCreditItem?.localClientRelationshipOwnerId,
                clientRelationshipOwner: getFullName(firstName, lastName),
                firstName,
                lastName,
                email: getValueOrBlank(salesCreditItem?.email),
                percentage: salesCreditItem?.percentage,
                officeName: salesCreditItem?.officeName,
              };
            },
          );
        setFilteredResults(existFilteredResults);
        addEntriesToFormKey(existFilteredResults);
      }
    }, [watchSalesCreditEntriesKey]);

    const addEntriesToFormKey = (
      results: SalesCreditTableRow[],
      isEntryDeleted = false,
    ) => {
      setValue(resultsFormKey, results, {
        shouldDirty: isEntryDeleted,
      });
    };

    const checkValidations = () => validateSalesCreditEntry();

    const validateSalesCreditEntry = (): SalesCreditTableRow | null => {
      const clientRelationshipOwner = getValues(clientRelationshipOwnerKey);
      const percentageValue = getValues(percentageKey);

      validateField(clientRelationshipOwner, clientRelationshipOwnerKey);
      validateField(percentageValue, percentageKey);

      if (isEmpty(clientRelationshipOwner) || isEmpty(percentageValue)) {
        return null;
      }
      const getSalesCreditProjectData = clientRelationshipOwnerList?.find(
        (salesCreditObj) => salesCreditObj.id === clientRelationshipOwner,
      );

      const firstName = getValueOrBlank(getSalesCreditProjectData?.firstName);
      const lastName = getValueOrBlank(getSalesCreditProjectData?.lastName);

      return {
        localClientRelationshipOwnerId: getValueOrBlank(
          getSalesCreditProjectData?.id,
        ),
        clientRelationshipOwner: getFullName(firstName, lastName),
        firstName,
        lastName,
        email: getValueOrBlank(getSalesCreditProjectData?.email),
        percentage: percentageValue,
        officeName: getValueOrBlank(getSalesCreditProjectData?.officeName),
      };
    };

    const validateField = (fieldValue: string | number, fieldKey: string) => {
      if (isEmpty(fieldValue)) {
        setError(fieldKey, {
          type: "custom",
          message: fetchValidationMessage(
            commonSiteCoreKeys.requiredValidation,
          ),
        });
      }
    };

    const addSalesCredit = () => {
      let newSalesCreditEntry: SalesCreditTableRow | null = null;
      newSalesCreditEntry = checkValidations();
      if (newSalesCreditEntry) {
        clearInputValues();
        const existFilteredResults = [...filteredResults, newSalesCreditEntry];
        sortFilterData(existFilteredResults);
        setFilteredResults(existFilteredResults);
        addEntriesToFormKey(existFilteredResults);
        setOnSalesCreditAddedCallback(!onSalesCreditAddedCallback);
      }
    };

    const clearInputValues = () => {
      setValue(clientRelationshipOwnerKey, "");
      setValue(percentageKey, "");
      setValue(relationshipOwnerOfficeKey, "");
    };

    const handleModalClose = () => {
      if (showDeleteModal) {
        setShowDeleteModal(false);
      }
    };

    const handleDeleteSalesCreditClick = () => {
      const selectedSalesCredit = selectedDeleteSalesCreditRef.current;
      if (selectedSalesCredit) {
        setShowDeleteModal(false);
        handleSalesCreditDeleteClick(selectedSalesCredit);
      }
    };

    const deleteSalesCreditAction = (row: SalesCreditTableRow) => {
      if (!showDeleteModal) {
        selectedDeleteSalesCreditRef.current = row;
        setShowDeleteModal(true);
      }
    };

    const handleSalesCreditAction = (row: SalesCreditTableRow) => {
      deleteSalesCreditAction(row);
    };

    return (
      <SalesCreditDetailsWrapper>
        <ProjectSalesCreditDetailsTable
          filteredResults={filteredResults}
          addSalesCredit={addSalesCredit}
          onDeleteSalesCredit={handleSalesCreditAction}
          onSalesCreditAddedCallback={onSalesCreditAddedCallback}
          clientRelationshipOwnerList={clientRelationshipOwnerList}
          onSalesCreditDeletedCallback={onSalesCreditDeletedCallback}
          pageId={pageId}
          isInViewMode={isInViewMode}
          showDefaultEntry={showDefaultLCROEntry}
        />
        {showDeleteModal && (
          <WarningConfirmationModal
            show={showDeleteModal}
            modalTitle={commonLabels.confirmation}
            secondaryButtonTitle={commonLabels.no}
            onClose={handleModalClose}
            onPrimaryButtonClick={handleDeleteSalesCreditClick}
            labelContent={fetchLabel(
              salesCreditSiteCoreKeys.salesCredit,
              salesCreditSiteCoreKeys.areYouSureDeleteClientRelationshipOwner,
            )}
          />
        )}
      </SalesCreditDetailsWrapper>
    );
  },
);
